import { FC, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useAppDispatch, useAppSelector } from '../../../../types';
import { NavigateToSessionStreetviewCommand } from '../../../../core/sessions/commands/NavigateToSessionStreetviewCommand';
import { sessionByIdQuery } from '../../../../core/sessions/queries';
import apiEndpoint from '../../../../core/legacy/utils/api';
import type { ImageProperties } from '../../../../core/domain/map';
import { useDraggableWithinParent } from '../../../../utils/use-draggable-within-parent';

type Props = {
    properties: ImageProperties | null;
};

const MiniImagePiP: FC<Props> = ({ properties }) => {
    const dispatch = useAppDispatch();
    const lastProperties = useRef<ImageProperties | null>(null);
    const session = useAppSelector(sessionByIdQuery(properties?.sessionId ?? lastProperties.current?.sessionId ?? ''));
    const { ref: dragRef, offset: dragOffset, handleDragStart } = useDraggableWithinParent();

    if (properties !== null) {
        lastProperties.current = properties;
    }

    const displayProperties = lastProperties.current;

    if (!displayProperties) return null;

    const handleGoToSession = () => {
        dispatch(
            NavigateToSessionStreetviewCommand({
                sessionId: displayProperties.sessionId,
                imageId: displayProperties.id,
            }),
        );
    };

    return (
        <Box
            ref={dragRef}
            sx={{
                position: 'absolute',
                bottom: 24,
                left: 24,
                transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                width: 320,
                height: 180,
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: 4,
                zIndex: 10,
                border: '2px solid white',
                bgcolor: 'black',
            }}
        >
            {/* Transparent drag handle strip across the top — above the image, below buttons */}
            <Box
                onMouseDown={handleDragStart}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 36,
                    zIndex: 10,
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                }}
            />
            <IconButton
                size="small"
                onClick={handleGoToSession}
                sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 11,
                    bgcolor: 'white',
                    '&:hover': { bgcolor: 'grey.100' },
                    padding: '4px',
                    boxShadow: 1,
                }}
            >
                <OpenInFullIcon fontSize="small" />
            </IconButton>
            <Box
                component="img"
                src={`${apiEndpoint}/sessions/${displayProperties.sessionId}/images/${displayProperties.imageIndex}.jpeg`}
                alt=""
                sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            />
            {session?.captureDate && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 6,
                        right: 8,
                        zIndex: 11,
                        bgcolor: 'rgba(0,0,0,0.45)',
                        color: 'white',
                        fontSize: '11px',
                        borderRadius: '3px',
                        px: '6px',
                        py: '2px',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                >
                    {new Date(session.captureDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </Box>
            )}
        </Box>
    );
};

export default MiniImagePiP;
